import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper function to extract company_id from token (like payroll)
const getCompanyIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    //console.error('No authentication token found');
    return null;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.company_id;
  } catch (error) {
    //console.error('Error parsing token:', error);
    return null;
  }
};

const StartSprintDialog = ({ 
  sprint, 
  projectKey, 
  onSprintStarted, 
  onSprintUpdated,
  open, 
  onClose, 
  mode = 'start'
}) => {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [duration, setDuration] = useState(2);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState('ACTIVE');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditMode = mode === 'edit';
  const isStartMode = mode === 'start';

  const canEditSprint = isStartMode || (isEditMode && sprint?.status === 'ACTIVE');

  useEffect(() => {
    if (sprint && open) {
      setName(sprint.name || '');
      setGoal(sprint.goal || '');
      setStatus(sprint.status || 'FUTURE');
      
      if (isEditMode) {
        if (sprint.startDate) {
          setStartDate(new Date(sprint.startDate));
        } else if (isStartMode) {
          setStartDate(new Date());
        }
        
        if (sprint.endDate) {
          setEndDate(new Date(sprint.endDate));
        }
        
        if (sprint.startDate && sprint.endDate) {
          const start = new Date(sprint.startDate);
          const end = new Date(sprint.endDate);
          const diffTime = Math.abs(end - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          const weeks = Math.ceil(diffDays / 7);
          
          if ([1, 2, 3, 4].includes(weeks)) {
            setDuration(weeks);
          } else {
            setDuration('custom');
          }
        } else {
          setDuration(2);
        }
      } else {
        setDuration(2);
        const today = new Date();
        setStartDate(today);
        
        const defaultEndDate = new Date(today);
        defaultEndDate.setDate(today.getDate() + (2 * 7) - 1);
        setEndDate(defaultEndDate);
      }
    }
  }, [sprint, open, mode]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    
    if (date && duration !== 'custom' && typeof duration === 'number') {
      const endDateValue = new Date(date);
      endDateValue.setDate(date.getDate() + (duration * 7) - 1);
      setEndDate(endDateValue);
    }
  };

  const handleDurationChange = (event) => {
    const newDuration = event.target.value;
    setDuration(newDuration);
    
    if (newDuration === 'custom') {
      return;
    }
    
    if (startDate && newDuration) {
      const endDateValue = new Date(startDate);
      endDateValue.setDate(startDate.getDate() + (newDuration * 7) - 1);
      setEndDate(endDateValue);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    setStatus(newStatus);
    
    if (newStatus === 'ACTIVE' && !startDate) {
      const today = new Date();
      setStartDate(today);
      
      if (typeof duration === 'number') {
        const defaultEndDate = new Date(today);
        defaultEndDate.setDate(today.getDate() + (duration * 7) - 1);
        setEndDate(defaultEndDate);
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Sprint name is required';
    }
    
    if (isStartMode || status === 'ACTIVE') {
      if (!startDate) {
        newErrors.startDate = 'Start date is required';
      }
      
      if (!endDate) {
        newErrors.endDate = 'End date is required';
      }
      
      if (startDate && endDate && startDate >= endDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }
    
    if (isEditMode && status === 'FUTURE' && startDate && endDate && startDate >= endDate) {
      newErrors.endDate = 'End date must be after start date';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !sprint) return;
    
    // Extract company_id from token (CRITICAL FIX)
    const company_id = getCompanyIdFromToken();
    if (!company_id) {
      setErrors({ submit: 'Authentication error. Please log in again.' });
      return;
    }
    
    try {
      setLoading(true);
      
      let calculatedDuration = null;
      if (startDate && endDate) {
        const diffTime = Math.abs(endDate - startDate);
        calculatedDuration = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }
      const token = localStorage.getItem('token');
      const updateData = {
        name: name.trim(),
        goal: goal.trim(),
        status: isStartMode ? 'ACTIVE' : status
      };
      
      if (startDate) {
        updateData.startDate = startDate.toISOString();
      }
      if (endDate) {
        updateData.endDate = endDate.toISOString();
      }
      if (calculatedDuration) {
        updateData.duration = calculatedDuration;
      }
      
      // Add company_id as query parameter (CRITICAL FIX)
      const response = await axios.put(
        
        `${API_URL}/api/sprints/${sprint.id}`, 
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (response.data && response.data.data) {
        const updatedSprint = {
          ...sprint,
          ...response.data.data,
          status: isStartMode ? 'ACTIVE' : response.data.data.status
        };
        
        if (isStartMode) {
          onSprintStarted(updatedSprint);
        } else {
          onSprintUpdated && onSprintUpdated(updatedSprint);
        }
        
        handleClose();
      }
    } catch (err) {
      //console.error(`Error ${isStartMode ? 'starting' : 'updating'} sprint:`, err);
      setErrors({ submit: `Failed to ${isStartMode ? 'start' : 'update'} sprint. Please try again.` });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setGoal('');
    setDuration(2);
    setStartDate(new Date());
    setEndDate(null);
    setStatus('FUTURE');
    setErrors({});
    onClose();
  };

  if (!sprint) return null;

  if (isEditMode && !canEditSprint) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Cannot Edit Sprint
            </Typography>
            <Chip
              label={sprint.status}
              size="small"
              color={sprint.status === 'FUTURE' ? 'default' : 'primary'}
              sx={{ 
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Only active sprints can be edited. Start the sprint first to make changes.
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
            <Typography variant="body2" color="warning.dark">
              <strong>Sprint Status:</strong> {sprint.status}
            </Typography>
            <Typography variant="body2" color="warning.dark" sx={{ mt: 1 }}>
              To edit this sprint, you need to start it first. Only active sprints can be modified.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const dialogTitle = isStartMode ? 'Start Sprint' : 'Edit Sprint';
  const dialogSubtitle = isStartMode 
    ? 'Set the sprint details and timeline to begin work.'
    : 'Update sprint details.';
  const submitButtonText = isStartMode ? 'Start Sprint' : 'Update Sprint';
  const loadingText = isStartMode ? 'Starting Sprint...' : 'Updating Sprint...';

  const isActive = status === 'ACTIVE';
  const isFuture = status === 'FUTURE';
  const isCompleted = status === 'COMPLETED';

  const areDatesDisabled = duration !== 'custom';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {dialogTitle}
          </Typography>
          {isEditMode && (
            <Chip
              label={status}
              size="small"
              color={isActive ? 'success' : isFuture ? 'default' : 'primary'}
              sx={{ 
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500
              }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {dialogSubtitle}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          <TextField
            label="Sprint Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          />
          
          <TextField
            label="Sprint Goal"
            placeholder="What do you want to achieve in this sprint?"
            fullWidth
            multiline
            rows={3}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            helperText="Optional: Describe the main objective of this sprint"
          />
          
          {isEditMode && (
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={handleStatusChange}
                disabled={false}
              >
                <MenuItem value="FUTURE">Future</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </Select>
              <FormHelperText>
                Choose the current status of the sprint
              </FormHelperText>
            </FormControl>
          )}
          
          {(isStartMode || (isEditMode && (isActive || (isFuture && (startDate || endDate))))) && (
            <>
              <FormControl fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={duration}
                  label="Duration"
                  onChange={handleDurationChange}
                >
                  <MenuItem value={1}>1 week</MenuItem>
                  <MenuItem value={2}>2 weeks</MenuItem>
                  <MenuItem value={3}>3 weeks</MenuItem>
                  <MenuItem value={4}>4 weeks</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
                <FormHelperText>
                  {areDatesDisabled ? "Dates are automatically calculated based on duration" : "Select 'Custom' to set dates manually"}
                </FormHelperText>
              </FormControl>
              
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                        error={!!errors.startDate}
                        helperText={errors.startDate || (areDatesDisabled ? "Auto-calculated" : "")}
                      />
                    )}
                    disabled={areDatesDisabled}
                    minDate={isStartMode || (isEditMode && isFuture) ? new Date() : undefined}
                  />
                  
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                        error={!!errors.endDate}
                        helperText={errors.endDate || (areDatesDisabled ? "Auto-calculated" : "")}
                      />
                    )}
                    disabled={areDatesDisabled}
                    minDate={startDate}
                  />
                </Box>
              </LocalizationProvider>
              
              {duration !== 'custom' && startDate && endDate && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Sprint Duration:</strong> {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1} days
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Timeline:</strong> {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                  </Typography>
                </Box>
              )}

              {duration === 'custom' && (
                <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="body2" color="info.main">
                    <strong>Custom Duration:</strong> You can manually set the start and end dates.
                  </Typography>
                </Box>
              )}
            </>
          )}
          
          {isEditMode && isFuture && !startDate && !endDate && (
            <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="body2" color="info.main">
                <strong>Future Sprint:</strong> Dates will be set when the sprint is started. You can optionally set them now.
              </Typography>
            </Box>
          )}
          
          {errors.submit && (
            <FormHelperText error>{errors.submit}</FormHelperText>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: '#0052CC',
            '&:hover': {
              backgroundColor: '#0747A6',
            }
          }}
        >
          {loading ? loadingText : submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartSprintDialog;